import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import { intelligenceV2 } from '@modules/aawz-intelligence-v2/constants/URL'
import { formatQueryParams } from '@utils/Format'
import { ERRO_INESPERADO } from '@modules/aawz-intelligence-v2/api/guardas'

/**
 * Memória curada, perfil do profissional, memória de cliente e do escritório
 * (spec-002-memoria-curada-e-perfil-do-profissional, [T016]).
 *
 * Contrato completo: `.specify/project-hermes-agent-design/
 * spec-002-memoria-curada-e-perfil-do-profissional/contracts/{api.yaml,
 * ui-components.md}`. HTTP sempre 200 — o status real vem em `r.status`
 * (padrão AAWZ), então nenhum método aqui trata rejeição de promise: o
 * `con.*` já captura e devolve o envelope `{status, message, value}`.
 */
// `ResourceMemoriaPerfil.put` (resource_memoria.py) lê `request.get_json`
// SOZINHO — sem fallback pra `request.form` — então o corpo TEM que ir como
// JSON. O default do `services/connection.js` é FormData (o `{json:true}` é
// opt-in, molde `modules/ai-framework/api/apiAiFrameworkInstances.js:19-25`):
// sem ele, `montarFormData` serializa o `patch` ANINHADO (`communication`)
// como a string literal "[object Object]" e o backend recebe `patch={}` →
// 400 (achado G-1 da review front spec-002, 29/08/2026). As demais escritas
// deste módulo (purge/desfazer/adicionarEscritorio) mandam só STRING e o
// resource as lê com `reqparse(location=['json','form'])` — funcionam com
// FormData, não precisam do opt-in (conferido rota a rota nesta revisão).
const ENVIAR_JSON = { json: true }

class APIMemoria extends APITemplate {
  /** GET /memoria — perfil + blocos pessoais + escritório em leitura + auditoria recente. */
  async listar() {
    const r = await con.get(intelligenceV2.memoria)
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /** PUT /memoria/perfil — patch das chaves reconhecidas do perfil estruturado. */
  async salvarPerfil(patch) {
    const r = await con.put(intelligenceV2.memoriaPerfil, patch, false, ENVIAR_JSON)
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /** DELETE /memoria/{id} — apaga uma entrada individual. */
  async remover({ id } = {}) {
    if (!id) {
      return this.buildErrorResponse({ message: 'id obrigatório', status: 400 })
    }
    const r = await con.delet(intelligenceV2.memoriaItem(id))
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /** GET /memoria/auditoria — trilha do titular, created_at DESC. */
  async auditoria({ limit = 100, offset = 0 } = {}) {
    const r = await con.get(
      intelligenceV2.memoriaAuditoria + formatQueryParams({ limit, offset })
    )
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /** POST /memoria/auditoria/{id}/desfazer — reversão em um clique. */
  async desfazer({ id } = {}) {
    if (!id) {
      return this.buildErrorResponse({ message: 'id obrigatório', status: 400 })
    }
    const r = await con.post(intelligenceV2.memoriaDesfazer(id), {})
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /** GET /memoria/export — export do titular em base64 (Art. 18 V). */
  async exportar() {
    const r = await con.get(intelligenceV2.memoriaExport)
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /** POST /memoria/purge — purge físico da memória do PRÓPRIO titular. */
  async purge({ confirmacao } = {}) {
    const r = await con.post(intelligenceV2.memoriaPurge, { confirmacao })
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /** GET /memoria/clientes/{id} — o que a IA sabe sobre um cliente. */
  async cliente({ id } = {}) {
    if (!id) {
      return this.buildErrorResponse({ message: 'id obrigatório', status: 400 })
    }
    const r = await con.get(intelligenceV2.memoriaCliente(id))
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /** POST /memoria/clientes/{id}/purge — purge físico da memória do cliente. */
  async purgeCliente({ id, confirmacao } = {}) {
    if (!id) {
      return this.buildErrorResponse({ message: 'id obrigatório', status: 400 })
    }
    const r = await con.post(intelligenceV2.memoriaClientePurge(id), { confirmacao })
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /** GET /memoria/escritorio — leitura para toda a sessão; pode_editar = gestor. */
  async escritorio() {
    const r = await con.get(intelligenceV2.memoriaEscritorio)
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /** POST /memoria/escritorio — grava um combinado do escritório (gestor). */
  async adicionarEscritorio({ content } = {}) {
    if (!content) {
      return this.buildErrorResponse({ message: 'content obrigatório', status: 400 })
    }
    const r = await con.post(intelligenceV2.memoriaEscritorio, { content })
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }

  /** POST /memoria/escritorio/purge — purge físico da memória do escritório (gestor). */
  async purgeEscritorio({ confirmacao } = {}) {
    const r = await con.post(intelligenceV2.memoriaEscritorioPurge, { confirmacao })
    return r || this.buildErrorResponse({ message: ERRO_INESPERADO })
  }
}

export const apiMemoria = new APIMemoria()
